import { useContext, useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useGroupApi } from 'api/group_by'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import { SampleSearchForm } from 'entities/sample'
import moment from 'moment'
import { useMemo } from 'react'
import { Flex } from 'components/shared/flex'
import { ThemeContext } from 'contexts/theme_context'
import { ButtonGroup, Table, ToggleButton } from 'react-bootstrap'
import { ContentHeader } from 'components/shared/content'
import { CheckboxField, SelectField } from 'components/shared/input'
import { selectYears } from 'utils/util'

type Props = {
    history: H.History
}

const SalesPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const theme = useContext(ThemeContext)
    const initDate = { start: moment().startOf('year').format('YYYY-MM-DD'), end: moment().endOf('year').format('YYYY-MM-DD') }
    const [currentState, setCurrentState] = useState<'arrival' | 'actual'>('arrival')

    const periodForm = useForm<{ year: string; month: string; byMonth: boolean }>({
        year: moment().format('YYYY'),
        month: moment().format('MM'),
        byMonth: false,
    })
    const bargraphSettingsForm = useForm<{ cumsum: boolean }>({ cumsum: false })

    const salesSearchForm = useForm<SampleSearchForm>({
        groupBy: 'month',
        aggregate: 'Sum("price")',
        aggregateAlias: 'sum',
        truncate: 'month',
        truncDateColumn: 'inspection_date',
        truncDateColumnType: 'datetime',
        inspectionDateAfter: initDate.start,
        inspectionDateBefore: initDate.end,
        resultStatus: 'OK,REFERENCE',
    })
    const salesApi = useGroupApi('samples', salesSearchForm)
    const institutionSalesSearchForm = useForm<SampleSearchForm>({
        groupBy: 'institution__name',
        aggregate: 'Sum("price")',
        aggregateAlias: 'sum',
        groupingValues: 'institution__id',
        inspectionDateAfter: initDate.start,
        inspectionDateBefore: initDate.end,
        resultStatus: 'OK,REFERENCE',
    })
    const institutionSalesApi = useGroupApi('samples', institutionSalesSearchForm)
    const institutionDiscountSearchForm = useForm<SampleSearchForm>({
        groupBy: 'institution__name',
        aggregate: 'Sum("discount")',
        aggregateAlias: 'sum',
        inspectionDateAfter: initDate.start,
        inspectionDateBefore: initDate.end,
        resultStatus: 'OK,REFERENCE',
    })
    const institutionDiscountApi = useGroupApi('samples', institutionDiscountSearchForm)

    const institutionItemGroupSearchForm = useForm<SampleSearchForm>({
        groupBy: 'institution__name',
        aggregate: 'Count("id")',
        aggregateAlias: 'count',
        groupingValues: 'institution__name',
        inspectionDateAfter: initDate.start,
        inspectionDateBefore: initDate.end,
        isStandard: false,
        resultStatus: 'OK,REFERENCE',
    })
    const institutionItemGroupApi = useGroupApi('samples', institutionItemGroupSearchForm)

    const productItemGroupSearchForm = useForm<SampleSearchForm>({
        groupBy: 'product_items__name',
        aggregate: 'Count("id")',
        aggregateAlias: 'count',
        groupingValues: 'product_items__color',
        inspectionDateAfter: initDate.start,
        inspectionDateBefore: initDate.end,
        isStandard: false,
        groupOrdering: 'product_items__id',
        resultStatus: 'OK,REFERENCE',
    })
    const productItemGroupApi = useGroupApi('samples', productItemGroupSearchForm)

    useEffectSkipFirst(() => {
        const forms = [salesSearchForm, institutionItemGroupSearchForm, institutionSalesSearchForm, institutionDiscountSearchForm, productItemGroupSearchForm]
        if (currentState === 'arrival') {
            forms.forEach((form, index) => {
                form.update((f) => {
                    delete f.sampleStatus
                    f.inspectionDateAfter = getPeriodAfter()
                    f.inspectionDateBefore = getPeriodBefore()
                    delete f.sendDateAfter
                    delete f.sendDateBefore
                    f.truncDateColumn = 'inspection_date'
                    if (index === 0) {
                        f.groupBy = periodForm.object.byMonth ? 'day' : 'month'
                        f.truncate = periodForm.object.byMonth ? 'day' : 'month'
                    }
                })
            })
        } else {
            forms.forEach((form, index) => {
                form.update((f) => {
                    f.sampleStatus = 'SENT'

                    f.sendDateAfter = getPeriodAfter()
                    f.sendDateBefore = getPeriodBefore()
                    delete f.inspectionDateAfter
                    delete f.inspectionDateBefore
                    f.truncDateColumn = 'send_date'
                    if (index === 0) {
                        f.groupBy = periodForm.object.byMonth ? 'day' : 'month'
                        f.truncate = periodForm.object.byMonth ? 'day' : 'month'
                    }
                })
            })
        }
    }, [currentState, periodForm.object])

    const getPeriodAfter = () => {
        return moment(`${periodForm.object.year}-${periodForm.object.month}-01`)
            .startOf(periodForm.object.byMonth ? 'month' : 'year')
            .format('YYYY-MM-DD')
    }

    const getPeriodBefore = () => {
        return moment(`${periodForm.object.year}-${periodForm.object.month}-01`)
            .endOf(periodForm.object.byMonth ? 'month' : 'year')
            .format('YYYY-MM-DD')
    }

    useEffect(() => {
        salesApi.execute()
        institutionItemGroupApi.execute()
        institutionSalesApi.execute()
        institutionDiscountApi.execute()
        productItemGroupApi.execute()
    }, [])

    const data = useMemo(() => {
        if (periodForm.object.byMonth) {
            let cumurate = 0
            const list = new Array(moment(`${periodForm.object.year}-${periodForm.object.month}-01`).daysInMonth()).fill(0).map((_, index) => {
                const currentDay = ('0' + (index + 1)).slice(-2)
                const date = `${periodForm.object.year}-${periodForm.object.month}-${currentDay}`
                const group = salesApi.response.groups.find((g) => g.day === date)
                if (!group) {
                    return { day: `${currentDay}`, sum: 0 }
                }
                const g = { ...group }
                const [__, ___, spDay] = g.day.split('-')
                g['day'] = `${spDay}`
                cumurate = cumurate + g.sum
                g['cumsum'] = cumurate
                return g
            })
            return list
        } else {
            let cumurate = 0
            const list = new Array(12).fill(0).map((_, index) => {
                const currentMonth = index + 1
                const currentStrMonth = ('0' + currentMonth).slice(-2)
                const date = `${periodForm.object.year}-${currentStrMonth}-01`
                const group = salesApi.response.groups.find((g) => g.month === date)
                if (!group) {
                    return { month: `${periodForm.object.year}/${currentStrMonth}`, sum: 0 }
                }
                const g = { ...group }
                const [spYear, spMonth] = g.month.split('-')
                g['month'] = `${spYear}/${spMonth}`
                cumurate = cumurate + g.sum
                g['cumsum'] = cumurate
                return g
            })
            return list
        }
    }, [salesApi.loading])

    const CustomTooltip = ({ active, payload, label, title, unit }: { active?: any; payload?: any; label?: any; title?: string; unit?: string }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{ backgroundColor: 'white', padding: 10 }} className="shadow">
                    <div>{`${label || payload[0].name}`}</div>
                    <div>
                        {title}: {payload[0].value.toLocaleString()}
                        {unit}
                    </div>
                </div>
            )
        } else {
            return <div></div>
        }
    }

    let sumPrice = 0
    let sumDiscount = 0
    let sumPurePrice = 0
    let sumItemCount = 0

    return (
        <>
            <ContentHeader
                headerTitle="売上"
                left={
                    <ButtonGroup>
                        <ToggleButton
                            id={`radio-arrival`}
                            type="radio"
                            variant={'outline-primary'}
                            name="radio"
                            value={'arrival'}
                            checked={currentState === 'arrival'}
                            onChange={(e) => setCurrentState('arrival')}
                        >
                            到着基準
                        </ToggleButton>
                        <ToggleButton
                            id={`radio-actual`}
                            type="radio"
                            variant={'outline-primary'}
                            name="radio"
                            value={'actual'}
                            checked={currentState === 'actual'}
                            onChange={(e) => setCurrentState('actual')}
                        >
                            発生基準
                        </ToggleButton>
                    </ButtonGroup>
                }
                right={
                    <div style={{ display: 'flex', width: periodForm.object.byMonth ? 330 : 250 }}>
                        <div style={{ width: 180, marginTop: 5 }}>
                            <CheckboxField checkboxLabel="月次集計" form={periodForm} attr="byMonth" />
                        </div>
                        <div style={{ marginRight: 10, width: 250 }}>
                            <SelectField className="" form={periodForm} attr="year" selectItems={selectYears()} />
                        </div>
                        {periodForm.object.byMonth && (
                            <div style={{ width: 150 }}>
                                <SelectField
                                    className=""
                                    form={periodForm}
                                    attr="month"
                                    selectItems={Array(12)
                                        .fill(0)
                                        .map((_, m) => ({ label: `${m + 1}月`, value: ('0' + (m + 1)).slice(-2) }))}
                                />
                            </div>
                        )}
                    </div>
                }
            />
            <Flex style={{ width: '100%', marginTop: 20, padding: 10, paddingTop: 20 }} className="shadow">
                <div style={{ width: '70%' }}>
                    <div style={{ marginLeft: 10 }}>
                        <CheckboxField form={bargraphSettingsForm} attr="cumsum" checkboxLabel="累積グラフを表示" />
                    </div>
                    <div style={{ fontSize: 15, color: '#666', marginBottom: 10, textAlign: 'center' }}>売上推移</div>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            width={500}
                            height={300}
                            data={data}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey={periodForm.object.byMonth ? 'day' : 'month'}
                                angle={-25}
                                textAnchor="end"
                                height={40}
                                tick={{ fontSize: 14 }}
                                interval={0}
                            />
                            <YAxis />

                            <Tooltip content={<CustomTooltip title={'売上'} unit="円" />} />
                            <Bar dataKey={bargraphSettingsForm.object.cumsum ? 'cumsum' : 'sum'} fill={theme.primary} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div style={{ width: '30%', marginTop: 25 }}>
                    <div style={{ fontSize: 15, color: '#666', marginBottom: 10, textAlign: 'center' }}>製品販売数</div>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart width={600} height={600}>
                            <Pie
                                data={productItemGroupApi.response.groups}
                                cx="50%"
                                cy="40%"
                                // labelLine={false}
                                outerRadius={100}
                                // fill="#8884d8"
                                dataKey="count"
                                nameKey="productItemsName"
                                label
                                // isAnimationActive={false}
                            >
                                {/* <LabelList dataKey="productItemsName" /> */}
                                {productItemGroupApi.response.groups.map((entry, index) => {
                                    return <Cell key={`cell-${index}`} fill={entry.productItemsColor} />
                                })}
                            </Pie>
                            <Legend></Legend>
                            <Tooltip></Tooltip>
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </Flex>
            <div style={{ padding: 10, marginTop: 20 }} className="shadow">
                <Table>
                    <thead>
                        <tr>
                            <th style={{ minWidth: 200 }}>病院・クリニック</th>
                            <th style={{ width: 120, textAlign: 'right' }}>個数</th>
                            <th style={{ width: 120, textAlign: 'right' }}>売上高</th>
                            <th style={{ width: 120, textAlign: 'right' }}>値引き</th>
                            <th style={{ width: 120, textAlign: 'right' }}>純売上高</th>
                        </tr>
                    </thead>
                    <tbody>
                        {institutionSalesApi.response.groups.map((g, index) => {
                            const discountGroup = institutionDiscountApi.response.groups.find((dg) => dg.institutionName === g.institutionName)
                            const discount = discountGroup?.sum || 0
                            const discountText = discount.toLocaleString()
                            const pureSales = g.sum - (discount || 0)
                            const pureSalesText = pureSales ? pureSales.toLocaleString() : '0'
                            const itemCount = institutionItemGroupApi.response.groups.find((i) => i.institutionName === g.institutionName)?.count || 0
                            sumPrice += g.sum
                            sumDiscount += discount
                            sumPurePrice += pureSales
                            sumItemCount += itemCount
                            return (
                                <tr key={'sales-' + index}>
                                    <td>{g.institutionName}</td>
                                    <td align="right">{itemCount}</td>
                                    <td align="right">{g.sum ? g.sum.toLocaleString() : '0'}</td>
                                    <td align="right">{discountText}</td>
                                    <td align="right">{pureSalesText}</td>
                                </tr>
                            )
                        })}
                        <tr style={{ backgroundColor: '#0802' }}>
                            <td>合計</td>
                            <td align="right">{sumItemCount.toLocaleString()}</td>
                            <td align="right">{sumPrice.toLocaleString()}</td>
                            <td align="right">{sumDiscount.toLocaleString()}</td>
                            <td align="right">{sumPurePrice.toLocaleString()}</td>
                        </tr>
                    </tbody>
                </Table>
            </div>
        </>
    )
}

export default withRouter(SalesPage)
